/* V207 — Dashboard tab: "Pluginy → AI Agenti" — API key management +
 * install snippets + WordPress plugin download.
 *
 * URL: /dashboard?tab=api-keys
 */
import { useEffect, useState } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const AGENTS = [
  { slug: "helpdesk", label: "Helpdesk", price: 79 },
  { slug: "review-reply", label: "Review Reply", price: 69 },
  { slug: "cart-recovery", label: "Cart Recovery", price: 89 },
  { slug: "product-desc", label: "Product Desc Optimizer", price: 69 },
  { slug: "newsletter", label: "Newsletter", price: 79 },
  { slug: "gdpr-watch", label: "GDPR Watch", price: 99 },
  { slug: "social-media", label: "Social Media", price: 79 },
  { slug: "review-moderator", label: "Review Moderator", price: 79 },
  { slug: "product-importer", label: "Product Importer", price: 99 },
  { slug: "pricing-intel", label: "Pricing Intel", price: 99 },
  { slug: "customer-onboarding", label: "Customer Onboarding", price: 79 },
  { slug: "behavior-analytics", label: "Behavior Analytics", price: 89 },
  { slug: "sales-hunter", label: "Sales Hunter", price: 99 },
  { slug: "competitive-manager", label: "Competitive Manager", price: 99 },
  { slug: "seo-pages-generator", label: "SEO Pages Generator", price: 129 },
];

const LOCALES = [
  { code: "sk", label: "Slovensky" },
  { code: "cs", label: "Čeština" },
  { code: "en", label: "English" },
  { code: "de", label: "Deutsch" },
  { code: "pl", label: "Polski" },
  { code: "hu", label: "Magyar" },
];

export default function AgentApiKeysTab() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [newKey, setNewKey] = useState(null); // shown once with secret
  const [creating, setCreating] = useState(false);
  const [label, setLabel] = useState("");
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [allowedDomains, setAllowedDomains] = useState("");
  const [snippetAgent, setSnippetAgent] = useState("helpdesk");
  const [snippetLocale, setSnippetLocale] = useState("sk");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const r = await axios.get(`${API}/me/api-keys`);
      setKeys(r.data.keys || []);
    } catch (e) { setErr(e.response?.data?.detail || "load_failed"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    setCreating(true); setErr(""); setNewKey(null);
    try {
      const domains = allowedDomains.split(",").map((d) => d.trim()).filter(Boolean);
      const r = await axios.post(`${API}/me/api-keys`, {
        label, agent_slugs: selectedAgents, allowed_domains: domains,
      });
      setNewKey(r.data);
      setLabel(""); setSelectedAgents([]); setAllowedDomains("");
      await load();
    } catch (e) { setErr(e.response?.data?.detail || "create_failed"); }
    finally { setCreating(false); }
  };

  const revoke = async (key) => {
    if (!window.confirm("Naozaj revoke tento API key? Recipient stratí prístup okamžite.")) return;
    try {
      await axios.delete(`${API}/me/api-keys/${key}`);
      await load();
    } catch (e) { setErr(e.response?.data?.detail || "revoke_failed"); }
  };

  const toggleAgent = (slug) => {
    setSelectedAgents((p) => p.includes(slug) ? p.filter((s) => s !== slug) : [...p, slug]);
  };

  const activeKey = keys.find((k) => k.status === "active");
  const snippetKey = newKey?.key || activeKey?.key || "pk_live_REPLACE_ME";
  const snippet = `<!-- TopRankPulse AI Agent: ${snippetAgent} -->
<script async
        src="${process.env.REACT_APP_BACKEND_URL}/api/static/embed/agent.js"
        data-agent="${snippetAgent}"
        data-key="${snippetKey}"
        data-locale="${snippetLocale}"></script>`;

  return (
    <div className="space-y-6 p-1" data-testid="api-keys-tab">
      <div>
        <h2 className="text-2xl font-black text-gray-900">🔑 API Keys & Plugins</h2>
        <p className="text-sm text-gray-500 mt-1">
          Vytvor API kľúče pre vloženie AI agentov na svoju stránku. Stiahni WordPress plugin alebo skopíruj JS snippet.
        </p>
      </div>

      {err && <div className="bg-rose-50 border border-rose-300 text-rose-700 p-3 text-sm">{err}</div>}

      {/* New key reveal */}
      {newKey && (
        <div className="bg-emerald-50 border-2 border-emerald-500 p-4" data-testid="new-key-reveal">
          <h3 className="font-black text-emerald-900 mb-2">✓ Nový API key vytvorený</h3>
          <p className="text-xs text-emerald-700 mb-3">
            <strong>Skopíruj `secret` teraz</strong> — neukáže sa znova. `key` (`pk_live_…`) môžeš vidieť aj neskôr.
          </p>
          <div className="bg-black text-emerald-300 font-mono text-xs p-3 break-all rounded">
            <div><strong className="text-gray-400">key:</strong> {newKey.key}</div>
            <div className="mt-1"><strong className="text-gray-400">secret:</strong> {newKey.secret}</div>
          </div>
          <button onClick={() => setNewKey(null)} className="mt-3 text-xs px-3 py-1 bg-emerald-600 text-white rounded">Zatvoriť</button>
        </div>
      )}

      {/* Create form */}
      <section className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
        <h3 className="font-black mb-3">➕ Vytvoriť nový API key</h3>
        <div className="space-y-3">
          <input value={label} onChange={(e) => setLabel(e.target.value)}
                 placeholder="Label (napr. 'eshop.sk – production')"
                 data-testid="api-key-label-input"
                 className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />

          <div>
            <label className="text-xs text-gray-600 font-bold uppercase tracking-wider">Allowed agents (prázdne = všetkých 15)</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1 mt-2">
              {AGENTS.map((a) => (
                <label key={a.slug} className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={selectedAgents.includes(a.slug)}
                         onChange={() => toggleAgent(a.slug)}
                         data-testid={`agent-checkbox-${a.slug}`} />
                  <span>{a.label} <span className="text-gray-400">€{a.price}</span></span>
                </label>
              ))}
            </div>
          </div>

          <input value={allowedDomains} onChange={(e) => setAllowedDomains(e.target.value)}
                 placeholder="Allowed domains (čiarka-separated, napr. 'eshop.sk, blog.eshop.sk')"
                 data-testid="api-key-domains-input"
                 className="w-full border border-gray-300 rounded px-3 py-2 text-sm" />

          <button onClick={create} disabled={creating}
                  data-testid="create-api-key-btn"
                  className="bg-purple-600 hover:bg-purple-500 text-white font-bold px-5 py-2 text-sm rounded disabled:opacity-50">
            {creating ? "Generujem…" : "🔑 Vygenerovať API key"}
          </button>
        </div>
      </section>

      {/* Existing keys */}
      <section className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
        <h3 className="font-black mb-3">🔐 Tvoje API keys ({keys.length})</h3>
        {loading && <div className="text-gray-500 text-sm">Načítavam…</div>}
        {!loading && keys.length === 0 && (
          <div className="text-gray-500 text-sm py-4 text-center">Zatiaľ žiadne API keys. Vytvor prvý ↑</div>
        )}
        {keys.length > 0 && (
          <div className="space-y-2" data-testid="api-keys-list">
            {keys.map((k) => (
              <div key={k.key} className="border border-gray-200 rounded p-3 text-xs" data-testid={`api-key-row-${k.key}`}>
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div>
                    <div className="font-bold text-gray-900">{k.label || "(no label)"}</div>
                    <div className="font-mono text-gray-600 mt-1">{k.key}</div>
                    <div className="text-gray-400 mt-1">
                      Agents: {(k.agent_slugs || []).join(", ") || "all 15"} ·
                      Domains: {(k.allowed_domains || []).join(", ") || "any"} ·
                      Usage 30d: <strong>{k.usage_30d || 0}</strong>/{k.monthly_limit || "∞"}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[9px] uppercase font-black px-2 py-0.5 rounded ${
                      k.status === "active" ? "bg-emerald-600 text-white" : "bg-rose-600 text-white"
                    }`}>{k.status}</span>
                    {k.status === "active" && (
                      <button onClick={() => revoke(k.key)}
                              data-testid={`revoke-${k.key}`}
                              className="text-[10px] border border-rose-300 text-rose-700 hover:bg-rose-50 px-2 py-1 rounded">
                        ✗ Revoke
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Snippet generator */}
      <section className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
        <h3 className="font-black mb-3">📋 JS Snippet Generator</h3>
        <p className="text-xs text-gray-500 mb-3">
          1-line script ktorý vložíš do <code>&lt;head&gt;</code> svojej stránky. Funguje na 85% web platforiem.
        </p>
        <div className="flex gap-2 flex-wrap mb-3">
          <select value={snippetAgent} onChange={(e) => setSnippetAgent(e.target.value)}
                  data-testid="snippet-agent-select"
                  className="border border-gray-300 rounded px-3 py-2 text-sm">
            {AGENTS.map((a) => <option key={a.slug} value={a.slug}>{a.label} (€{a.price}/mo)</option>)}
          </select>
          <select value={snippetLocale} onChange={(e) => setSnippetLocale(e.target.value)}
                  data-testid="snippet-locale-select"
                  className="border border-gray-300 rounded px-3 py-2 text-sm">
            {LOCALES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
          </select>
        </div>
        <pre className="bg-gray-900 text-amber-300 font-mono text-xs p-4 rounded overflow-x-auto whitespace-pre-wrap">{snippet}</pre>
        <button onClick={() => { navigator.clipboard.writeText(snippet); alert("Skopírované!"); }}
                data-testid="copy-snippet-btn"
                className="mt-3 bg-purple-600 hover:bg-purple-500 text-white font-bold px-4 py-2 text-sm rounded">
          📋 Kopírovať snippet
        </button>
      </section>

      {/* WordPress plugin download */}
      <section className="bg-white border border-gray-200 rounded-2xl p-5 shadow-sm">
        <h3 className="font-black mb-3">📦 WordPress Plugin (.zip)</h3>
        <p className="text-xs text-gray-500 mb-3">
          Pre WordPress klientov: stiahni .zip plugin, nahraj do WP Admin → Plugins → Upload, aktivuj, vlož API key. Hotovo za 3 minúty.
        </p>
        <ol className="text-xs text-gray-600 mb-4 list-decimal list-inside space-y-1">
          <li>Stiahni plugin tlačidlom nižšie</li>
          <li>WP Admin → Plugins → Add New → Upload Plugin → vyber .zip → Install</li>
          <li>Activate plugin</li>
          <li>Settings → TopRankPulse Agents → vlož API key (zhora)</li>
          <li>Buď zapni "Floating bubble" alebo použi shortcode <code>[trp_agent slug="helpdesk"]</code></li>
        </ol>
        <a href={`${API}/plugin/wordpress/toprankpulse-agents.zip`}
           data-testid="wp-plugin-download"
           className="inline-block bg-blue-600 hover:bg-blue-500 text-white font-bold px-5 py-2 text-sm rounded">
          ⬇️ Stiahnuť WordPress plugin
        </a>
      </section>

      {/* Compatibility checker link */}
      <section className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-2xl p-5">
        <h3 className="font-black mb-2">🔍 Compatibility Scanner</h3>
        <p className="text-xs text-gray-600 mb-3">
          Neistý či funguje na klientovej stránke? Otestuj zadarmo (bez login-u).
        </p>
        <a href="/agent-compat" target="_blank" rel="noopener noreferrer"
           data-testid="compat-link"
           className="inline-block bg-purple-600 hover:bg-purple-500 text-white font-bold px-5 py-2 text-sm rounded">
          🔍 Otvoriť scanner
        </a>
      </section>
    </div>
  );
}
